import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./Lockerlocations.css";
import Footer from "./Footer";
import CommonPopup from "./CommonPopup";
import useInactivityRedirect from "./useInactivityRedirect";
import LoadingSpinner from "../components/LoadingSpinner";
import settings from "../config/settings.json";

const Lockerlocations = () => {
  const { t } = useTranslation("lockerlocations");
  const navigate = useNavigate();
  const { mainCode } = useParams();
  const UNITCODE = sessionStorage.getItem("UNITCODE");
  const KIOSK_NM = sessionStorage.getItem("KIOSK_NM");

  const [PosArray, setPosArray] = useState({});
  const [lockedLockers, setLockedLockers] = useState([]);
  const [lockerMeta, setLockerMeta] = useState({});
  const [selectedLocker, setSelectedLocker] = useState(null);
  const [scale, setScale] = useState(1);
  const [loading, setLoading] = useState(true);

  const [popupState, setPopupState] = useState({
    visible: false,
    message: "",
    onConfirm: () => {},
    onClose: () => {},
    confirmText: t("yes"),
    cancelText: t("no"),
  });

  const {
    showPopup,
    confirmRedirect,
    cancelRedirect,
    remainingSeconds,
  } = useInactivityRedirect({
    inactivityTimeout: 2 * 60 * 1000,
    popupTimeout: 15 * 1000,
    redirectTo: "/",
  });

  const gridRef = useRef(null);

  useEffect(() => {
    const initialize = async () => {
      if (!sessionStorage.getItem("USER_ID")) {
        const userId = "user_" + Math.random().toString(36).substring(2, 10);
        sessionStorage.setItem("USER_ID", userId);
      }

      const userId = sessionStorage.getItem("USER_ID");
      const lockerNo = sessionStorage.getItem("SELECTED_LOCKER");

      if (lockerNo && userId) {
        try {
          await fetch("/api/lockerlock/release", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ lockerNo, userId, unitCode: UNITCODE }),
          });
          sessionStorage.removeItem("SELECTED_LOCKER");
        } catch (err) {
          console.warn("잠금 해제 실패:", err);
        }
      }

      try {
		const boxRes = await fetch("/api/lockerboxRoutes/" + UNITCODE, {
		  headers: {
			"x-api-token": settings.API_TOKEN
		  }
		});
        const boxData = await boxRes.json();

        const newPos = {};
        const meta = {};
        boxData.forEach(item => {
          const rectArray = item.LOCKER_RECT.split(',').map(Number);
          newPos[item.LOCKER_NO] = rectArray;
          meta[item.LOCKER_NO] = {
            status: item.LOCKER_STATUS,
            isUsed: item.IS_USED,
          };
        });

        setPosArray(newPos);
        setLockerMeta(meta);

        const lockRes = await fetch("/api/lockerlock/locked?unitcode=" + UNITCODE);
        const lockData = await lockRes.json();
        setLockedLockers(lockData.locked || []);
      } catch (err) {
        console.error("데이터 로딩 실패:", err);
      } finally {
        setTimeout(() => setLoading(false), 400);
      }
    };

    if (UNITCODE) {
      initialize();
    }
  }, [UNITCODE]);

  useEffect(() => {

    if (!UNITCODE || !mainCode) {
      alert(t("unauthorized_access"));
      navigate("/"); 
    } 

    const container = gridRef.current;
    if (!container) return;

    const originalWidth = 800;
    const originalHeight = 400;

    const resizeObserver = new ResizeObserver(() => {
      const widthScale = container.offsetWidth / originalWidth;
      const heightScale = container.offsetHeight / originalHeight;
      const newScale = Math.min(widthScale, heightScale);
      setScale(Math.max(0.4, Math.min(0.8, newScale)));
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  const handleConfirm = async () => {
    const userId = sessionStorage.getItem("USER_ID");

    try {
      const res = await fetch("/api/lockerlock/select", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lockerNo: selectedLocker, userId, unitCode: UNITCODE }),
      });

      const result = await res.json();

      if (result.success) {
        sessionStorage.setItem("SELECTED_LOCKER", selectedLocker);
        navigate("/ReservationInfo/" + selectedLocker, { state: { mainCode } });
      } else {
        showAlert(t("already_selected"));
      }
    } catch (err) {
      console.error("잠금 요청 실패:", err);
      showAlert(t("error_lock_request"));
    }
  };

  const showAlert = (message) => {
    setPopupState((prev) => ({
      ...prev,
      visible: true,
      message,
      onConfirm: () => setPopupState((prev) => ({ ...prev, visible: false })),
      onClose: () => setPopupState((prev) => ({ ...prev, visible: false })),
      confirmText: t("yes"),
      cancelText: "",
    }));
  };

  const Lockerlocations_SelectClick = () => {
    if (!selectedLocker) {
      showAlert(t("please_select_locker"));
    } else if (lockedLockers.includes(selectedLocker)) {
      showAlert(t("already_selected"));
    } else {
      if (mainCode === "F" || mainCode === "O") {
        const message = mainCode === "F"
          ? `<p><span class='highlight'>${selectedLocker}${t("locker_number")}</span></p><p>${t("confirm_end_use")}</p>`
          : `<p><span class='highlight'>${selectedLocker}${t("locker_number")}</span></p><p>${t("confirm_temp_open")}</p>`;

        setPopupState({
          visible: true,
          message,
          onConfirm: () => navigate(`/PasswordInput/${selectedLocker}`, { state: { mainCode } }),
          onClose: () => setPopupState(prev => ({ ...prev, visible: false })),
          confirmText: t("yes"),
          cancelText: t("no"),
        });
      } else {
        showReservationPopup();
      }
    }
  };

  const showReservationPopup = () => {
    setPopupState({
      visible: true,
      message: `<p><span class='highlight'>${selectedLocker}${t("locker_number")}</span></p><p>${t("confirm_reservation")}</p>`,
      onConfirm: () => {
        setPopupState(prev => ({ ...prev, visible: false }));
        handleConfirm();
      },
      onClose: () => setPopupState(prev => ({ ...prev, visible: false })),
      confirmText: t("yes"),
      cancelText: t("no"),
    });
  };

  return (
    <div className="wrap">
      {loading && <LoadingSpinner message="Loading..." />}

      <header>
        <div className="title" onClick={() => navigate("/")}> 
          <h1 dangerouslySetInnerHTML={{ __html: t("title_select_locker") }} />
        </div>
        <button className="back-button" onClick={() => window.history.back()}>
          ←
        </button>
      </header>

      <div className="locker-info">
        <div className="locker-status-bar">
          <div className="status-box">
              <div className="label">{t("location")}</div>
              <div className="value2">{KIOSK_NM}</div>
          </div>
          <div className="status-box">
            <div className="label">{t("status_available")}</div>
            <div className="value2">
			  {Object.entries(PosArray).filter(([no]) => {
				if (no === "0") return false; // 제어부는 제외

				const meta = lockerMeta[no] || {};
				const isLocked = lockedLockers.includes(`${UNITCODE}_${no}`);
				const isFree = !meta.isUsed;

				return isFree && !isLocked;
			  }).length}
            </div>
          </div>
          <div className="status-box">
            <div className="label">{t("status_selected")}</div>
            <div className="value2">{selectedLocker || t("none")}</div>
          </div>
			<div className="legend-box">
			  {mainCode === "R" && (
				<>
				  <div className="legend-item">
					<span>{t("legend_used")}</span>
					<span className="legend used"></span>
				  </div>
				  <div className="legend-item">
					<span>{t("legend_selected")}</span>
					<span className="legend selected"></span>
				  </div>
				</>
			  )}

			  {(mainCode === "F" || mainCode === "O") && (
				<>
				  <div className="legend-item">
					<span>{t("legend_used")}</span>
					<span className="legend" style={{ background: "#546A8D" }}></span>
				  </div>
				  <div className="legend-item">
					<span>{t("legend_selected")}</span>
					<span className="legend selected"></span>
				  </div>
				</>
			  )}
			</div>
        </div>
      </div>

      <main className="content">
        <div className="locker-wrap">
          <div className="locker-content">
            <section className="locker-grid" ref={gridRef}>
              <div className="grid-container">
                {Object.entries(PosArray).map(([no, rect]) => {
                  const [left, top, right, bottom] = rect;
                  const width = (right - left) * scale;
                  const height = (bottom - top) * scale;
                  const scaledLeft = left * scale;
                  const scaledTop = top * scale;

                  const style = {
                    left: `${scaledLeft}px`,
                    top: `${scaledTop}px`,
                    width: `${width}px`,
                    height: `${height}px`,
                    fontSize: `${Math.max(14, 18 * scale)}px`,
                  };

                  const isLocked = lockedLockers.includes(`${UNITCODE}_${no}`);
                  const meta = lockerMeta[no] || {};
                  const isUsed = meta.isUsed;

                  return (
                    <div
                      key={no}
						className={`locker-box ${
						  no === "0"
							? "controller"
							: isLocked
							? "locked"
							: (mainCode === "F" || mainCode === "O")
							  ? isUsed
								? selectedLocker === no
								  ? "selected"
								  : "f-used"
								: "f-disabled"
							  : isUsed
							  ? "used"
							  : selectedLocker === no
							  ? "selected"
							  : "available"
						}`}
                      style={style}
						onClick={() => {
						  if (no === "0" || isLocked) return;

						  const isUsed = meta.isUsed;

						  // 조건 분기: R은 'used' 못 누르고, F/O는 'used'만 누를 수 있음
						  if (
							(mainCode === "R" && isUsed) || 
							((mainCode === "F" || mainCode === "O") && !isUsed)
						  ) return;

						  setSelectedLocker(no);
						}} >
                      {no === "0" ? t("controller") : no}
                    </div>
                  );
                })}
              </div>
            </section>

            <div className="locker-controls">
              <button className="select-btn" onClick={Lockerlocations_SelectClick}>
                {t("select")}
              </button>
            </div>
          </div>
        </div>
      </main>

      <CommonPopup {...popupState} />

      {showPopup && (
        <CommonPopup
          visible={true}
          message={`${t("continue_question")}<br/><span style="font-weight:700;color:red">(${remainingSeconds} ${t("seconds")})</span>${t("auto_redirect")}`}
          onConfirm={confirmRedirect}
          onClose={cancelRedirect}
          confirmText="예"
          cancelText="아니오"
        />
      )}

      <Footer />
    </div>
  );
};

export default Lockerlocations;